import React from "react";
import * as RechartsPrimitive from "recharts";

import { cn } from "../cn";

/**
 * Recharts 2 adaptation of the shadcn chart primitive. It uses prefixed Tailwind v3 utilities and the
 * repository's light theme; dashboard components render their own legends. `ResponsiveContainer`
 * measures its parent, while jsdom reports a 0×0 box, so unit tests cannot assert chart geometry.
 */

export type ChartConfig = Record<
  string,
  {
    label?: React.ReactNode;
    icon?: React.ComponentType;
    color?: string;
  }
>;

type ChartContextProps = { config: ChartConfig };

const ChartContext = React.createContext<ChartContextProps | null>(null);

function useChart() {
  const context = React.useContext(ChartContext);
  if (!context) {
    throw new Error("useChart must be used within a <ChartContainer />");
  }
  return context;
}

export function ChartContainer({
  id,
  className,
  children,
  config,
  ...props
}: React.ComponentProps<"div"> & {
  config: ChartConfig;
  children: React.ComponentProps<typeof RechartsPrimitive.ResponsiveContainer>["children"];
}) {
  const uniqueId = React.useId();
  const chartId = `chart-${id ?? uniqueId.replace(/:/g, "")}`;

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-slot="chart"
        data-chart={chartId}
        className={cn(
          "tw-flex tw-justify-center tw-text-xs",
          "[&_.recharts-cartesian-axis-tick_text]:tw-fill-muted-foreground",
          "[&_.recharts-cartesian-grid_line[stroke='#ccc']]:tw-stroke-border/50",
          "[&_.recharts-curve.recharts-tooltip-cursor]:tw-stroke-border",
          "[&_.recharts-dot[stroke='#fff']]:tw-stroke-transparent",
          "[&_.recharts-layer]:tw-outline-none",
          "[&_.recharts-polar-grid_[stroke='#ccc']]:tw-stroke-border",
          "[&_.recharts-radial-bar-background-sector]:tw-fill-muted",
          "[&_.recharts-rectangle.recharts-tooltip-cursor]:tw-fill-muted",
          "[&_.recharts-reference-line_[stroke='#ccc']]:tw-stroke-border",
          "[&_.recharts-sector]:tw-outline-none",
          "[&_.recharts-sector[stroke='#fff']]:tw-stroke-transparent",
          "[&_.recharts-surface]:tw-outline-none",
          className,
        )}
        {...props}
      >
        <ChartStyle id={chartId} config={config} />
        <RechartsPrimitive.ResponsiveContainer width="100%" height="100%">
          {children}
        </RechartsPrimitive.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  );
}

/** Emits one `--color-<series>` custom property per configured series, scoped to this chart instance. */
export function ChartStyle({ id, config }: { id: string; config: ChartConfig }) {
  const colorConfig = Object.entries(config).filter(([, item]) => item.color);
  if (!colorConfig.length) {
    return null;
  }
  return (
    <style
      dangerouslySetInnerHTML={{
        __html: `[data-chart=${id}] {\n${colorConfig.map(([key, item]) => `  --color-${key}: ${item.color ?? ""};`).join("\n")}\n}`,
      }}
    />
  );
}

export const ChartTooltip = RechartsPrimitive.Tooltip;

type TooltipContentProps = RechartsPrimitive.TooltipProps<number | string, number | string> &
  Omit<React.ComponentProps<"div">, "content" | "ref"> & {
    hideLabel?: boolean;
    hideIndicator?: boolean;
    indicator?: "line" | "dot" | "dashed";
    nameKey?: string;
    labelKey?: string;
  };

export function ChartTooltipContent({
  active,
  payload,
  className,
  indicator = "dot",
  hideLabel = false,
  hideIndicator = false,
  label,
  labelFormatter,
  labelClassName,
  formatter,
  color,
  nameKey,
  labelKey,
}: TooltipContentProps) {
  const { config } = useChart();

  const tooltipLabel = React.useMemo(() => {
    if (hideLabel || !payload?.length) {
      return null;
    }
    const [item] = payload;
    const key = `${labelKey ?? item?.dataKey ?? item?.name ?? "value"}`;
    const itemConfig = getPayloadConfigFromPayload(config, item, key);
    const value = !labelKey && typeof label === "string" ? (config[label]?.label ?? label) : itemConfig?.label;

    if (labelFormatter) {
      return <div className={cn("tw-font-medium", labelClassName)}>{labelFormatter(value, payload)}</div>;
    }
    if (!value) {
      return null;
    }
    return <div className={cn("tw-font-medium", labelClassName)}>{value}</div>;
  }, [label, labelFormatter, payload, hideLabel, labelClassName, config, labelKey]);

  if (!active || !payload?.length) {
    return null;
  }

  const nestLabel = payload.length === 1 && indicator !== "dot";

  return (
    <div
      className={cn(
        "tw-grid tw-min-w-32 tw-items-start tw-gap-1.5 tw-rounded-lg tw-border tw-border-solid tw-border-border/50 tw-bg-background tw-px-2.5 tw-py-1.5 tw-text-xs tw-shadow-lg",
        className,
      )}
    >
      {!nestLabel ? tooltipLabel : null}
      <div className="tw-grid tw-gap-1.5">
        {payload
          .filter((item) => item.type !== "none")
          .map((item, index) => {
            const key = `${nameKey ?? item.name ?? item.dataKey ?? "value"}`;
            const itemConfig = getPayloadConfigFromPayload(config, item, key);
            const indicatorColor = color ?? itemConfig?.color ?? (item.payload as { fill?: string } | undefined)?.fill ?? item.color;

            return (
              <div
                key={index}
                className={cn("tw-flex tw-w-full tw-flex-wrap tw-items-stretch tw-gap-2 [&>svg]:tw-size-2.5 [&>svg]:tw-text-muted-foreground", indicator === "dot" && "tw-items-center")}
              >
                {formatter && item?.value !== undefined && item.name ? (
                  formatter(item.value, item.name, item, index, payload)
                ) : (
                  <>
                    {itemConfig?.icon ? (
                      <itemConfig.icon />
                    ) : (
                      !hideIndicator && (
                        <div
                          className={cn("tw-shrink-0 tw-rounded-sm tw-border-[var(--color-border)] tw-bg-[var(--color-bg)]", {
                            "tw-size-2.5": indicator === "dot",
                            "tw-w-1": indicator === "line",
                            "tw-w-0 tw-border-[1.5px] tw-border-dashed tw-bg-transparent": indicator === "dashed",
                            "tw-my-0.5": nestLabel && indicator === "dashed",
                          })}
                          style={{ "--color-bg": indicatorColor, "--color-border": indicatorColor } as React.CSSProperties}
                        />
                      )
                    )}
                    <div className={cn("tw-flex tw-flex-1 tw-justify-between tw-leading-none", nestLabel ? "tw-items-end" : "tw-items-center")}>
                      <div className="tw-grid tw-gap-1.5">
                        {nestLabel ? tooltipLabel : null}
                        <span className="tw-text-muted-foreground">{itemConfig?.label ?? item.name}</span>
                      </div>
                      {item.value != null && (
                        <span className="tw-font-medium tw-tabular-nums tw-text-foreground">{typeof item.value === "number" ? item.value.toLocaleString() : String(item.value)}</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}

/** Helper to extract item config from a payload. */
function getPayloadConfigFromPayload(config: ChartConfig, payload: unknown, key: string) {
  if (typeof payload !== "object" || payload === null) {
    return undefined;
  }

  const payloadPayload = "payload" in payload && typeof payload.payload === "object" && payload.payload !== null ? payload.payload : undefined;

  let configLabelKey: string = key;

  if (key in payload && typeof payload[key as keyof typeof payload] === "string") {
    configLabelKey = payload[key as keyof typeof payload] as string;
  } else if (payloadPayload && key in payloadPayload && typeof payloadPayload[key as keyof typeof payloadPayload] === "string") {
    configLabelKey = payloadPayload[key as keyof typeof payloadPayload] as string;
  }

  return configLabelKey in config ? config[configLabelKey] : config[key];
}
